import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { Select } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import UiContent from '../../../Components/Common/UiContent';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import OChartCard from '../../../Components/OElements/OChartCard';
import { getEstablishmenQuery } from '../../../helpers/establishment/backend_helper';
import { getInspectionTypeList as getInspectionTypeListApi } from '../../../helpers/inspection_type/api_helper';
import { getInspectionDashboardSummary, getInspectionsByAuthorityYear } from '../../../helpers/inspection/api_helper';

import InspectionsByAuthorityChart from './Sections/InspectionsByAuthorityChart';
import InspectionsByYearCenterAuthorityChart from './Sections/InspectionsByYearCenterAuthorityChart';
import BreachByImpactChart from './Sections/BreachByImpactChart';
import BreachByRiskChart from './Sections/BreachByRiskChart';
import BreachByActivityStatusChart from './Sections/BreachByActivityStatusChart';
import BreachByCategoryChart from './Sections/BreachByCategoryChart';
import BreachByResponsibleAreaChart from './Sections/BreachByResponsibleAreaChart';
import BreachByEvidenceTypeChart from './Sections/BreachByEvidenceTypeChart';

const formatDate = d => {
	if (!d) return undefined;
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const FiscalizacionDashboard = () => {
	// ── Local master lists ────────────────────────────────────────
	const [establishmentList, setEstablishmentList] = useState([]);
	const [inspectionTypeList, setInspectionTypeList] = useState([]);

	// ── Filters ────────────────────────────────────────────────────
	const [selectedEstablishment, setSelectedEstablishment] = useState(null);
	const [selectedInspectionType, setSelectedInspectionType] = useState(null);
	const [dateRange, setDateRange] = useState([null, null]);

	// ── Dashboard data ─────────────────────────────────────────────
	const [dashboardData, setDashboardData] = useState(null);
	const [inspectionsByAuthorityYearData, setInspectionsByAuthorityYearData] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	// ── Fetch masters on mount ─────────────────────────────────────
	useEffect(() => {
		getEstablishmenQuery({
			filters: { disable: { $eq: false } },
			pagination: { page: 1, pageSize: 1000 },
			fields: ['_id', 'name'],
			sort: ['name:asc'],
		}).then(res => {
			setEstablishmentList(res.data || res || []);
		});

		getInspectionTypeListApi({
			fields: ['_id', 'name'],
		}).then(res => {
			setInspectionTypeList(res.data || res || []);
		});
	}, []);

	// ── Fetch dashboard summary when filters change ────────────────
	const fetchDashboard = useCallback(async () => {
		setIsLoading(true);
		try {
			const params = {};
			if (selectedEstablishment) params.establishment_id = selectedEstablishment;
			if (selectedInspectionType) params.inspection_type_id = selectedInspectionType;
			if (dateRange[0]) params.date_from = formatDate(dateRange[0]);
			if (dateRange[1]) params.date_to = formatDate(dateRange[1]);

			const [summaryRes, authorityYearRes] = await Promise.all([
				getInspectionDashboardSummary(params),
				getInspectionsByAuthorityYear(params),
			]);
			setDashboardData(summaryRes);
			if (authorityYearRes) setInspectionsByAuthorityYearData(authorityYearRes);
		} catch {
			setDashboardData(null);
		} finally {
			setIsLoading(false);
		}
	}, [selectedEstablishment, selectedInspectionType, dateRange]);

	useEffect(() => {
		fetchDashboard();
	}, [fetchDashboard]);

	// ── Derive available filter options from dashboard response ────
	const availableEstablishments = useMemo(() => {
		const series = dashboardData?.inspections_by_authority_establishment?.series || [];
		const names = new Set(series.map(s => s.name));
		return (establishmentList || [])
			.filter(e => !names.size || names.has(e.name))
			.map(e => ({ value: e._id, label: e.name }))
			.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
	}, [establishmentList, dashboardData]);

	const availableInspectionTypes = useMemo(() => {
		return (inspectionTypeList || [])
			.map(o => ({ value: o._id, label: o.name }))
			.sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
	}, [inspectionTypeList]);

	// ── Breadcrumb ─────────────────────────────────────────────────
	const breadcrumbLinks = [
		{ to: '#', label: 'Inicio', icon: 'icon-house' },
		{ to: '#', label: 'Fiscalización', icon: 'icon-clipboard-check' },
	];

	return (
		<React.Fragment>
			<UiContent />
			<div className='page-content'>
				<Container fluid>
					<BreadCrumb title='Panel de Fiscalización' links={breadcrumbLinks} icon={'icon-layout-dashboard'} />

					{/* ── Global Filters ─────────────────────────────────── */}
					<Card className='mb-3' style={{ borderRadius: '16px' }}>
						<CardBody className='p-2'>
							<div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
								<span style={{ fontWeight: 500, fontSize: '11px' }}>Filtros:</span>
								<Select
									placeholder='Todos los Establecimientos'
									data={availableEstablishments}
									value={selectedEstablishment}
									onChange={setSelectedEstablishment}
									clearable
									searchable
									size='xs'
									radius='xl'
									style={{ minWidth: '220px', maxWidth: '300px' }}
									styles={{ input: { fontSize: '14px' } }}
								/>
								<Select
									placeholder='Todos los Tipos'
									data={availableInspectionTypes}
									value={selectedInspectionType}
									onChange={setSelectedInspectionType}
									clearable
									searchable
									size='xs'
									radius='xl'
									style={{ minWidth: '200px', maxWidth: '280px' }}
									styles={{ input: { fontSize: '14px' } }}
								/>
								<DatePickerInput
									type='range'
									placeholder='Rango de Fechas'
									value={dateRange}
									onChange={setDateRange}
									clearable
									size='xs'
									radius='xl'
									valueFormat='DD/MM/YYYY'
									style={{ minWidth: '240px', maxWidth: '320px' }}
									styles={{ input: { fontSize: '14px' } }}
								/>
							</div>
						</CardBody>
					</Card>

					{/* ── Row 1: 2 charts side by side ───────────────────── */}
					<Row className='mb-3'>
						<Col xs={12} lg={6}>
							<OChartCard
								title='Fiscalizaciones por Entidad y Establecimiento'
								isLoading={false}
								isEmpty={false}
							>
								<InspectionsByAuthorityChart
									data={dashboardData?.inspections_by_authority_establishment}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
						<Col xs={12} lg={6}>
							<OChartCard
								title='Fiscalizaciones por Año y Entidad'
								isLoading={false}
								isEmpty={false}
							>
								<InspectionsByYearCenterAuthorityChart
									data={inspectionsByAuthorityYearData}
									isLoading={isLoading} 
								/>
							</OChartCard>
						</Col>
					</Row>

					{/* ── Row 2: 3 Donuts ────────────────────────────────── */}
					<Row className='mb-3'>
						<Col md={4}>
							<OChartCard title='Incumplimientos por Impacto' isLoading={false} isEmpty={false}>
								<BreachByImpactChart
									data={dashboardData?.breach_by_impact}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
						<Col md={4}>
							<OChartCard title='Incumplimientos por Clasificación de Riesgo' isLoading={false} isEmpty={false}>
								<BreachByRiskChart
									data={dashboardData?.breach_by_risk_classification}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
						<Col md={4}>
							<OChartCard title='Incumplimientos por Estado de Actividad' isLoading={false} isEmpty={false}>
								<BreachByActivityStatusChart
									data={dashboardData?.breach_by_activity_status}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
					</Row>

					{/* ── Row 3: 3 Bars ──────────────────────────────────── */}
					<Row className='mb-3'>
						<Col md={4}>
							<OChartCard title='Incumplimientos por Categoría' isLoading={false} isEmpty={false}>
								<BreachByCategoryChart
									data={dashboardData?.breach_by_category}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
						<Col md={4}>
							<OChartCard title='Incumplimientos por Área Responsable' isLoading={false} isEmpty={false}>
								<BreachByResponsibleAreaChart
									data={dashboardData?.breach_by_responsible_area}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
						<Col md={4}>
							<OChartCard title='Incumplimientos por Tipo de Evidencia' isLoading={false} isEmpty={false}>
								<BreachByEvidenceTypeChart
									data={dashboardData?.breach_by_evidence_type}
									isLoading={isLoading}
								/>
							</OChartCard>
						</Col>
					</Row>
				</Container>
			</div>
		</React.Fragment>
	);
};

export default FiscalizacionDashboard;
